<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisSourceHealthCard — health snapshot for one external source/provider
 * (Quinto/Zap/portais/KYC/webhook providers/Data Registry sources).
 *
 * Self-contained card (never nested inside another card). Shows aggregate
 * metrics only — no PII, tokens or payloads pass through here, so there is
 * nothing to redact. An unknown/unrecognized `status` falls back to the
 * neutral tone and is never rendered as healthy.
 */
const props = defineProps({
  name: { type: String, default: '' },
  // Accept any string: unknown provider statuses map to the neutral tone
  // internally. A strict enum validator would warn on real backend values.
  status: { type: String, default: 'unknown' },
  lastRunAt: { type: [String, Number, Date], default: '' },
  latencyMs: { type: Number, default: null },
  successCount: { type: Number, default: null },
  failureCount: { type: Number, default: null },
  coverage: { type: [Number, Object], default: null },
  stale: { type: Boolean, default: false },
  canSync: { type: Boolean, default: true },
  icon: { type: String, default: '' },
});

const emit = defineEmits(['sync']);

const STATUS_TONES = Object.freeze({
  healthy: 'positive',
  syncing: 'info',
  stale: 'warning',
  disabled: 'neutral',
  error: 'danger',
  unknown: 'neutral',
});

const RELATIVE_DIVISIONS = Object.freeze([
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
]);

const EMPTY_VALUE = '—';

const { t, locale } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-source-health-${instance?.uid || 'default'}`;
const titleId = `${baseId}-title`;
const coverageId = `${baseId}-coverage`;

const statusKey = computed(() => {
  const value = String(props.status || '').toLowerCase();
  return STATUS_TONES[value] ? value : 'unknown';
});
const statusTone = computed(() => STATUS_TONES[statusKey.value] || 'neutral');
const statusLabel = computed(() => {
  switch (statusKey.value) {
    case 'healthy':
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.HEALTHY');
    case 'syncing':
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.SYNCING');
    case 'stale':
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.STALE');
    case 'disabled':
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.DISABLED');
    case 'error':
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.ERROR');
    default:
      return t('LAIS_SOURCE_HEALTH_CARD.STATUS.UNKNOWN');
  }
});
const isSyncing = computed(() => statusKey.value === 'syncing');
const displayName = computed(
  () => props.name || t('LAIS_SOURCE_HEALTH_CARD.UNKNOWN_SOURCE')
);

const lastRunDate = computed(() => {
  if (props.lastRunAt === '' || props.lastRunAt == null) return null;
  const date = new Date(props.lastRunAt);
  return Number.isNaN(date.getTime()) ? null : date;
});
const lastRunTitle = computed(() =>
  lastRunDate.value ? lastRunDate.value.toLocaleString() : undefined
);

const buildRelativeFormatter = () => {
  const tag = String(locale?.value || '').replace('_', '-') || undefined;
  try {
    return new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
  } catch {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  }
};

const formatRelative = date => {
  const formatter = buildRelativeFormatter();
  let duration = (date.getTime() - Date.now()) / 1000;
  const division = RELATIVE_DIVISIONS.find(item => {
    if (Math.abs(duration) < item.amount) return true;
    duration /= item.amount;
    return false;
  });
  return formatter.format(Math.round(duration), division.unit);
};

const lastRunRelative = computed(() =>
  lastRunDate.value
    ? formatRelative(lastRunDate.value)
    : t('LAIS_SOURCE_HEALTH_CARD.NEVER')
);

const latencyDisplay = computed(() => {
  if (props.latencyMs == null || !Number.isFinite(Number(props.latencyMs))) {
    return EMPTY_VALUE;
  }
  const ms = Number(props.latencyMs);
  return ms < 1000
    ? t('LAIS_SOURCE_HEALTH_CARD.LATENCY_MS', { value: Math.round(ms) })
    : t('LAIS_SOURCE_HEALTH_CARD.LATENCY_S', {
        value: (ms / 1000).toFixed(1),
      });
});

const successDisplay = computed(() =>
  props.successCount == null
    ? EMPTY_VALUE
    : new Intl.NumberFormat().format(props.successCount)
);
const failureDisplay = computed(() =>
  props.failureCount == null
    ? EMPTY_VALUE
    : new Intl.NumberFormat().format(props.failureCount)
);
const hasFailures = computed(
  () => Number.isFinite(Number(props.failureCount)) && props.failureCount > 0
);
const hasSuccesses = computed(
  () => Number.isFinite(Number(props.successCount)) && props.successCount > 0
);

const clamp = value => Math.max(0, Math.min(100, value));
const hasCoverage = computed(() => props.coverage != null);
const coveragePercent = computed(() => {
  const value = props.coverage;
  if (value == null) return 0;
  if (typeof value === 'object') {
    const total = Number(value.total) || 0;
    if (total <= 0) return 0;
    return clamp((Number(value.value || 0) / total) * 100);
  }
  return clamp(Number(value) || 0);
});
const coverageLabel = computed(() => {
  const value = props.coverage;
  if (value == null) return EMPTY_VALUE;
  if (typeof value === 'object') {
    return `${new Intl.NumberFormat().format(
      Number(value.value || 0)
    )}/${new Intl.NumberFormat().format(Number(value.total || 0))}`;
  }
  return `${Math.round(coveragePercent.value)}%`;
});
const coverageFill = computed(() => `${coveragePercent.value}%`);

const isSyncDisabled = computed(() => !props.canSync || isSyncing.value);
const syncLabel = computed(() =>
  isSyncing.value
    ? t('LAIS_SOURCE_HEALTH_CARD.SYNCING')
    : t('LAIS_SOURCE_HEALTH_CARD.SYNC')
);

const handleSync = event => {
  if (isSyncDisabled.value) {
    event.preventDefault();
    return;
  }
  emit('sync', event);
};
</script>

<template>
  <article
    role="group"
    :aria-labelledby="titleId"
    :data-status="statusKey"
    class="flex flex-col gap-4 overflow-hidden rounded-lais border border-lais-border-soft bg-lais-bg-card p-5 shadow-lais-card"
  >
    <header class="flex items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2.5">
        <span
          v-if="icon"
          class="size-5 shrink-0 text-lais-icon"
          :class="icon"
          aria-hidden="true"
        />
        <h3
          :id="titleId"
          class="truncate font-redHatDisplay text-lais-heading-sm font-semibold leading-tight text-lais-text"
        >
          {{ displayName }}
        </h3>
      </div>
      <LaisStatusBadge
        :tone="statusTone"
        :label="statusLabel"
        :title="statusLabel"
        dot
      />
    </header>

    <dl class="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div class="min-w-0 rounded-lais-md bg-lais-bg-soft px-3 py-2">
        <dt
          class="text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
        >
          {{ t('LAIS_SOURCE_HEALTH_CARD.LAST_RUN') }}
        </dt>
        <dd class="mt-1 flex flex-wrap items-center gap-1.5">
          <span
            :title="lastRunTitle"
            class="truncate text-lais-body-sm font-semibold text-lais-text-strong"
          >
            {{ lastRunRelative }}
          </span>
          <span
            v-if="stale"
            class="inline-flex items-center rounded-full border border-lais-warning/40 bg-lais-warning/15 px-1.5 py-0.5 text-lais-label-xs font-semibold text-lais-text"
          >
            {{ t('LAIS_SOURCE_HEALTH_CARD.STALE') }}
          </span>
        </dd>
      </div>

      <div class="min-w-0 rounded-lais-md bg-lais-bg-soft px-3 py-2">
        <dt
          class="text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
        >
          {{ t('LAIS_SOURCE_HEALTH_CARD.LATENCY') }}
        </dt>
        <dd
          class="mt-1 truncate text-lais-body-sm font-semibold text-lais-text-strong"
        >
          {{ latencyDisplay }}
        </dd>
      </div>

      <div class="min-w-0 rounded-lais-md bg-lais-bg-soft px-3 py-2">
        <dt
          class="text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
        >
          {{ t('LAIS_SOURCE_HEALTH_CARD.SUCCESSES') }}
        </dt>
        <dd
          class="mt-1 flex items-center gap-1.5 truncate text-lais-body-sm font-semibold"
          :class="
            hasSuccesses ? 'text-lais-mint-dark' : 'text-lais-text-strong'
          "
        >
          <span
            class="i-lucide-circle-check size-3.5 shrink-0"
            aria-hidden="true"
          />
          {{ successDisplay }}
        </dd>
      </div>

      <div class="min-w-0 rounded-lais-md bg-lais-bg-soft px-3 py-2">
        <dt
          class="text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
        >
          {{ t('LAIS_SOURCE_HEALTH_CARD.FAILURES') }}
        </dt>
        <dd
          data-test="failure-value"
          class="mt-1 flex items-center gap-1.5 truncate text-lais-body-sm font-semibold"
          :class="hasFailures ? 'text-lais-danger' : 'text-lais-text-strong'"
        >
          <span
            class="i-lucide-circle-x size-3.5 shrink-0"
            aria-hidden="true"
          />
          {{ failureDisplay }}
        </dd>
      </div>
    </dl>

    <div v-if="hasCoverage" class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between gap-2">
        <span
          :id="coverageId"
          class="text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
        >
          {{ t('LAIS_SOURCE_HEALTH_CARD.COVERAGE') }}
        </span>
        <span class="text-lais-body-sm font-semibold text-lais-text-strong">
          {{ coverageLabel }}
        </span>
      </div>
      <div
        role="progressbar"
        :aria-labelledby="coverageId"
        :aria-valuenow="Math.round(coveragePercent)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuetext="coverageLabel"
        class="h-1.5 w-full overflow-hidden rounded-full bg-lais-bg-soft"
      >
        <div
          class="h-full rounded-full bg-lais-mint transition-[width]"
          :style="{ width: coverageFill }"
        />
      </div>
    </div>

    <div class="flex items-center justify-end">
      <button
        type="button"
        data-test="sync-button"
        class="inline-flex min-h-9 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 py-2 font-redHatText text-lais-body-sm font-semibold text-white shadow-lais-soft transition-colors hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:bg-lais-primary-600 disabled:opacity-60 [.lais-dark_&]:text-lais-primary-deep"
        :disabled="isSyncDisabled"
        :aria-busy="isSyncing ? 'true' : undefined"
        :aria-label="syncLabel"
        @click="handleSync"
      >
        <span
          v-if="isSyncing"
          class="i-lucide-loader-circle size-4 shrink-0 animate-spin"
          aria-hidden="true"
        />
        <span
          v-else
          class="i-lucide-refresh-cw size-4 shrink-0"
          aria-hidden="true"
        />
        <span>{{ syncLabel }}</span>
      </button>
    </div>
  </article>
</template>
